/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createLogger } from '@/lib/logger';
import { deleteCookiesByPrefix } from '@/lib/cookie-utils.client';

const logger = createLogger();

/** DOM id of the injected Attentive loader `<script>`, used to dedupe injection. */
export const ATTENTIVE_SCRIPT_ID = 'attentive-tag';

/** DOM id of the `cdn.attn.tv` fallback `<script>`, injected only if the primary host fails. */
export const ATTENTIVE_FALLBACK_SCRIPT_ID = 'attentive-tag-fallback';

/**
 * Cookie name prefixes Attentive sets, removed on a best-effort basis when the shopper declines
 * the marketing category. Mirrors the SFRA implementation
 * (`org_smg/cartridges/app_core_org/cartridge/client/default/js/privacy/onetrust.js`).
 */
export const ATTENTIVE_COOKIE_PREFIXES = ['__attentive', '_attn_', 'attentive_cco', 'attntv_mstore_email'];

/** The subset of the Attentive tag runtime API this integration calls. */
export interface AttentiveAnalyticsApi {
    enable?: () => void;
    disable?: () => void;
}

declare global {
    interface Window {
        /**
         * Attentive runtime API. The loader installs this synchronously — the `analytics` methods
         * start life as queue-pushers and are replayed once `unified-tag.js` lands.
         */
        attentive?: {
            version?: string;
            analytics?: AttentiveAnalyticsApi;
        };
        /**
         * Host the loader serves sign-up-unit creatives from (`growblackmagic-us.attn.tv` for Black
         * Magic). Set by the loader; read here only for debugging.
         */
        __attentive_domain?: string;
    }
}

/**
 * Module-level cache so a remount (or a second mount in dev StrictMode) reuses the in-flight
 * injection instead of adding a second `<script>`. `src`/`fallbackSrc` are read once, on the first
 * call — they come from config and are stable for the life of the document.
 */
let injection: Promise<boolean> | null = null;

/** Reset the injection cache. Test-only. */
export function resetAttentiveInjectionForTests(): void {
    injection = null;
}

/**
 * Inject the Attentive loader, falling back to a secondary host if the primary one fails.
 *
 * Attentive ships this as a snippet with an inline `onError="…"` attribute, which our CSP blocks
 * (inline handlers aren't covered by the nonce and `script-src` has no `'unsafe-inline'`) — hence
 * the programmatic `onerror`. The injected script deliberately carries **no nonce**: the root
 * loader's nonce is only valid for the SSR-rendered HTML, and on a client navigation the loader
 * returns a fresh nonce with no accompanying CSP header. Both hosts are allowlisted in
 * `script-src` instead (see `config.server.ts`), same as Adobe Launch and Sierra.
 *
 * @returns Whether a loader script finished loading. Never rejects.
 */
export function injectAttentiveTag(src: string, fallbackSrc?: string): Promise<boolean> {
    if (typeof document === 'undefined' || !src) {
        return Promise.resolve(false);
    }

    injection ??= new Promise<boolean>((resolve) => {
        if (document.getElementById(ATTENTIVE_SCRIPT_ID)) {
            resolve(true);
            return;
        }

        const script = document.createElement('script');
        script.id = ATTENTIVE_SCRIPT_ID;
        script.async = true;
        script.src = src;
        script.onload = () => resolve(true);
        script.onerror = () => {
            // Same URL for both means there is nothing to fall back to (e.g. an environment that
            // overrides `src` to the cdn host, which is already the configured fallback).
            if (!fallbackSrc || fallbackSrc === src) {
                logger.error('[Attentive] Tag failed to load and no distinct fallback is configured', { src });
                resolve(false);
                return;
            }

            logger.warn('[Attentive] Primary tag host failed, loading fallback', { src, fallbackSrc });
            const fallback = document.createElement('script');
            fallback.id = ATTENTIVE_FALLBACK_SCRIPT_ID;
            fallback.async = true;
            fallback.src = fallbackSrc;
            fallback.onload = () => resolve(true);
            fallback.onerror = () => {
                logger.error('[Attentive] Fallback tag host also failed', { fallbackSrc });
                resolve(false);
            };
            document.head.appendChild(fallback);
        };

        document.head.appendChild(script);
    });

    return injection;
}

/**
 * Turn Attentive's analytics/tracking layer on or off.
 *
 * The tag itself always loads so that sign-up units (the acquisition popup) can render — only
 * tracking is consent-gated. This matches how SFRA drives Attentive from OneTrust and Attentive's
 * own documented `analytics.enable()` / `analytics.disable()` API.
 */
export function setAttentiveAnalyticsConsent(granted: boolean): void {
    const analytics = typeof window === 'undefined' ? undefined : window.attentive?.analytics;
    if (!analytics) {
        return;
    }
    if (granted) {
        analytics.enable?.();
    } else {
        analytics.disable?.();
    }
}

/**
 * Best-effort removal of Attentive's cookies, for when the shopper explicitly declines the
 * targeting category. Called by the OneTrust consent bridge (src/components/onetrust).
 *
 * Note this cannot deliver "no Attentive cookies": the tag re-sets several of them on its next
 * interaction, and it sets seven identity cookies before consent is collected at all. Genuinely
 * reaching zero would mean not loading the tag — see ai-docs/patterns/third-party-scripts.md.
 */
export function clearAttentiveCookies(): void {
    deleteCookiesByPrefix(ATTENTIVE_COOKIE_PREFIXES);
}
